import React from 'react';
import { ArrowRight } from 'lucide-react';
const Logo = () => <img src="https://cdn.magicpatterns.com/uploads/635dXvyehukv1voVp8uDzR/Union_(6).png" alt="Proximitty" className="h-7 w-auto" data-id="element-0" />;
export function App() {
  return <div className="min-h-screen py-12 flex justify-center items-start font-['Open_Sans',_sans-serif] text-slate-800 bg-[#F7F4F0]" data-id="element-1">
      {/* A4 Paper Container */}
      <div className="w-[816px] min-h-[1056px] bg-[#F7FAFD] shadow-2xl flex flex-col relative" data-id="element-2">
        {/* HEADER */}
        <header className="px-10 py-5 flex justify-between items-center bg-[#F8F7F6]" data-id="element-3">
          <Logo data-id="element-4" />
          <div className="text-right" data-id="element-5">
            <div className="font-medium text-sm font-['Tiro_Gurmukhi',_serif] text-[#0282C7]" data-id="element-6">
              AI agents for business loan servicing
            </div>
            <div className="text-xs mt-0.5 font-['Tiro_Gurmukhi',_serif] text-[#0C0C0C]" data-id="element-7">
              Compliance and reliable first by design
            </div>
          </div>
        </header>

        {/* HERO SECTION */}
        <section className="px-10 py-6 bg-[#F8F7F6]" data-id="element-8">
          <h1 className="text-[26px] leading-[1.15] text-slate-900 mb-8 max-w-[95%] font-[500] font-['Tiro_Gurmukhi',_serif]" data-id="element-9">
            Originate 10x more loans by automating your spreading and servicing
            end-to-end
          </h1>
          <p className="max-w-[95%] font-[400] text-[#0F0E0E] font-['Tiro_Gurmukhi',_serif] text-[14px]" data-id="element-10">
            Proximitty automates the entire commercial loan servicing lifecycle
            — from document collection and client back-and-forth to financial
            spreading, covenant tracking and early warning — so your team can
            stop servicing and start originating. We reduce the time to service
            from 10 hours down to minutes, with an audit log of every agent
            action.
          </p>
        </section>

        {/* MOCK UI SECTION */}
        <section className="px-8 py-3 bg-[#F8F7F6] flex justify-center" data-id="element-11">
          <div className="w-[95%] border border-slate-200 rounded-lg overflow-hidden shadow-sm bg-white" data-id="element-12">
            <img src="https://cdn.magicpatterns.com/uploads/d7xFLnEyHd6tYRjRErtM6A/Screenshot_2026-04-02_at_21.31.48.png" alt="Proximitty Spreading — Borrowing Base analysis view" className="w-full h-auto block" data-id="element-13" />
          </div>
        </section>

        {/* VALUE PROPS */}
        <section className="px-10 py-5 grid grid-cols-3 gap-6 bg-[#F8F7F6]" data-id="element-14">
          <div data-id="element-15">
            <h3 className="mb-3 font-['Tiro_Gurmukhi',_serif] text-[16px] font-[900] text-[#000000]" data-id="element-16">
              We automate 100% of spreading with our human-in-the-loop AI agents
            </h3>
            <p className="text-[13px] leading-relaxed font-['Tiro_Gurmukhi',_serif] font-[500] text-[#0C0C0CB1]" data-id="element-17">
              From raw documents to key ratios - parsing, spreading, and
              borrowing base calculation run entirely within our platform using
              your bank's configurable models. We verify each output with our
              team of credit risk experts - ensuring &gt;98% accuracy.
            </p>
          </div>
          <div data-id="element-18">
            <h3 className="font-bold mb-3 text-[16px] font-['Tiro_Gurmukhi',_serif] text-[#000000]" data-id="element-19">
              Our agents are trained to spread on loans from a wide variety of
              industries
            </h3>
            <p className="text-[13px] leading-relaxed font-['Tiro_Gurmukhi',_serif] text-[#0C0C0CB1]" data-id="element-20">
              Our state of the art models are trained to spread across a wide
              variety of industries, e.g., healthcare to commercial real estate
              to SBA and tax returns. Our latest computer vision models are able
              to parse blurry pictures.
            </p>
          </div>
          <div data-id="element-21">
            <h3 className="font-bold mb-3 text-[16px] font-['Tiro_Gurmukhi',_serif] text-[#000000]" data-id="element-22">
              An AI co-pilot that monitors your portfolio around the clock
            </h3>
            <p className="text-[13px] leading-relaxed font-['Tiro_Gurmukhi',_serif] text-[#0C0C0CB1]" data-id="element-23">
              Automatically tracks covenant thresholds, flags debt coverage
              deterioration, and surfaces early warning signals before they hit
              your aging report. Query any loan or portfolio at any time — it
              analyzes, explains, and proposes next actions.
            </p>
          </div>
        </section>

        {/* HOW IT WORKS + ROI */}
        <section className="px-10 py-5 flex-1 bg-[#F8F7F6]" data-id="element-24">
          <div className="flex gap-5" data-id="element-25">
            {/* How it works - left column */}
            <div className="w-[70%]" data-id="element-26">
              <div className="text-[#0284C7] font-bold tracking-wider mb-3 uppercase text-base font-['Tiro_Gurmukhi',_serif]" data-id="element-27">
                how it works
              </div>

              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm" data-id="element-28">
                <div className="px-6 py-5 flex flex-col gap-5" data-id="element-29">
                  {/* Step 1 */}
                  <div className="flex items-start gap-4" data-id="element-30">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm shrink-0" data-id="element-31">
                      1
                    </div>
                    <div data-id="element-32">
                      <div className="flex items-center gap-2 mb-1" data-id="element-33">
                        <h4 className="font-bold text-[14px] text-slate-900 font-['Tiro_Gurmukhi',_serif]" data-id="element-34">
                          Monitors & triggers
                        </h4>
                        <span className="bg-emerald-50 text-emerald-600 text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider" data-id="element-35">
                          Continuous
                        </span>
                      </div>
                      <p className="text-[12px] text-slate-500 leading-relaxed font-['Tiro_Gurmukhi',_serif]" data-id="element-36">
                        Tracks your portfolio in real time. Ticklers fire
                        automatically when financials are due, covenants
                        approach thresholds, or risk signals emerge.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-4" data-id="element-37">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm shrink-0" data-id="element-38">
                      2
                    </div>
                    <div data-id="element-39">
                      <h4 className="font-bold text-[14px] text-slate-900 mb-1 font-['Tiro_Gurmukhi',_serif]" data-id="element-40">
                        Collects documents from borrowers
                      </h4>
                      <p className="text-[12px] text-slate-500 leading-relaxed font-['Tiro_Gurmukhi',_serif]" data-id="element-41">
                        Our AI agents reach out to borrowers via email, request
                        the right documents, and actually can identify missing
                        or incorrect data and re-request information from
                        borrowers - no analyst needed to chase.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-4" data-id="element-42">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm shrink-0" data-id="element-43">
                      3
                    </div>
                    <div data-id="element-44">
                      <h4 className="font-bold text-[14px] text-slate-900 mb-1 font-['Tiro_Gurmukhi',_serif]" data-id="element-45">
                        Parses any document - even blurry ones
                      </h4>
                      <p className="text-[12px] text-slate-500 leading-relaxed font-['Tiro_Gurmukhi',_serif]" data-id="element-46">
                        Our state of the art vision models can read a variety of
                        different statements, e.g., tax returns, AR aging
                        reports, rent rolls, WIP schedules, financial statements
                        - we can even handle blurry / inconsistent reports.
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex items-start gap-4" data-id="element-47">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm shrink-0" data-id="element-48">
                      4
                    </div>
                    <div data-id="element-49">
                      <h4 className="font-bold text-[14px] text-slate-900 mb-1 font-['Tiro_Gurmukhi',_serif]" data-id="element-50">
                        Spreads with your business logic across a variety of
                        sector loans
                      </h4>
                      <p className="text-[12px] text-slate-500 leading-relaxed font-['Tiro_Gurmukhi',_serif]" data-id="element-51">
                        Applies your eligibility rules, add-back policies, and
                        industry-specific formulas — healthcare payer
                        classification, contractor WIP, CRE NOI — end-to-end,
                        without human intervention.
                      </p>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className="flex items-start gap-4" data-id="element-52">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm shrink-0" data-id="element-53">
                      5
                    </div>
                    <div data-id="element-54">
                      <h4 className="font-bold text-[14px] text-slate-900 mb-1 font-['Tiro_Gurmukhi',_serif]" data-id="element-55">
                        Full audit trail for every decision
                      </h4>
                      <p className="text-[12px] text-slate-500 leading-relaxed font-['Tiro_Gurmukhi',_serif]" data-id="element-56">
                        Every extraction, adjustment, and calculation logged and
                        queryable. Examiner-ready compliance output by default;
                        and you can ask our agent anything about the portfolio
                        at any time.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ROI - right column */}
            <div className="w-[30%] flex flex-col" data-id="element-57">
              <div className="text-[#0284C7] font-bold tracking-wider mb-3 uppercase text-base font-['Tiro_Gurmukhi',_serif]" data-id="element-58">
                &nbsp;
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm flex-1" data-id="element-59">
                <div className="px-5 py-5 flex flex-col gap-6" data-id="element-60">
                  <h4 className="font-bold text-[15px] text-slate-900 font-['Tiro_Gurmukhi',_serif]" data-id="element-61">
                    ROI for a $2bn AUM bank:
                  </h4>

                  <div data-id="element-62">
                    <div className="text-[28px] font-bold text-emerald-600 leading-tight" data-id="element-63">
                      $2.1m
                    </div>
                    <div className="text-[12px] text-slate-500 font-['Tiro_Gurmukhi',_serif] mt-1" data-id="element-64">
                      savings
                    </div>
                  </div>

                  <div data-id="element-65">
                    <div className="text-[28px] font-bold text-emerald-600 leading-tight" data-id="element-66">
                      25%
                    </div>
                    <div className="text-[12px] text-slate-500 font-['Tiro_Gurmukhi',_serif] mt-1" data-id="element-67">
                      more loans originated
                    </div>
                  </div>

                  <div data-id="element-68">
                    <div className="text-[28px] font-bold text-emerald-600 leading-tight" data-id="element-69">
                      80%
                    </div>
                    <div className="text-[12px] text-slate-500 font-['Tiro_Gurmukhi',_serif] mt-1" data-id="element-70">
                      quicker underwriting
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="px-10 py-6 flex justify-between items-center mt-auto border-t border-slate-100 bg-[#F8F7F6]" data-id="element-71">
          <div data-id="element-72">
            <div className="font-semibold text-sm mb-1 text-[#0382C7]" data-id="element-73">
              Trusted by publicly traded fintechs and banks.
            </div>
            <div className="text-[#0284C7] text-xs font-medium" data-id="element-74">
              Request a pilot today →
            </div>
          </div>
          <div className="flex gap-10 text-right" data-id="element-75">
            <div data-id="element-76">
              <div className="font-bold text-sm text-slate-800" data-id="element-77">Wye Yew Ho</div>
              <div className="text-slate-500 text-xs" data-id="element-78">CEO</div>
              <div className="text-slate-500 text-xs mt-1" data-id="element-79">
                wyeyew@proximitty.ai
              </div>
              <div className="text-slate-500 text-xs" data-id="element-80">(929) 638 6764</div>
            </div>
            <div data-id="element-81">
              <div className="font-bold text-sm text-slate-800" data-id="element-82">
                Zixuan Zhang
              </div>
              <div className="text-slate-500 text-xs" data-id="element-83">CTO</div>
              <div className="text-slate-500 text-xs mt-1" data-id="element-84">
                zi@proximitty.ai
              </div>
              <div className="text-slate-500 text-xs" data-id="element-85">(651) 246 8938</div>
            </div>
          </div>
        </footer>
      </div>
    </div>;
}